import { compareVersions } from 'compare-versions'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'

import backgroundImage1 from '@/assets/wecheckLights/1.png'
import backgroundImage2 from '@/assets/wecheckLights/2.png'
import { AppSidebar } from '@/components/AppSidebar/AppSidebar'
import { Loader } from '@/components/Loader/Loader'
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { AppRoutes } from '@/constants/appRoutes'
import env from '@/env/env'
import { useCheckSession } from '@/hooks/Auth/useCheckSession'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useStatusGuard } from '@/hooks/StatusGuard/useStatusGuard'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import useSmallMobileMediaQuery from '@/hooks/useSmallMobileMediaQuery'
import useTabletMediaQuery from '@/hooks/useTabletMediaQuery'
import { selectForceReloadVersion } from '@/store/api/Config/Config.selector'
import { PersonaInquiryStatusEnum } from '@/store/api/Persona/Persona.types'
import { useLazyUserInfoQuery } from '@/store/api/Users/Users.query'
import {
  selectIsPending,
  selectUserFirstLetter,
  selectUserFullname,
  selectUserInfo,
} from '@/store/api/Users/Users.selector'
import { setUserInfo } from '@/store/api/Users/Users.slice'

import { ForceReloadCard } from './components/ForceReloadCard/ForceReloadCard'

export const LeftSideBar = () => {
  const location = useLocation()
  const locationPathname = location.pathname

  const dispatch = useAppDispatch()

  const isMobileQuery = useMobileMediaQuery()
  const isSmallMobile = useSmallMobileMediaQuery()
  const isTablet = useTabletMediaQuery()

  const isSmallDevice = isMobileQuery || isSmallMobile || isTablet

  const remoteConfigForceReloadVersion = useSelector(selectForceReloadVersion)
  const userInfo = useAppSelector(selectUserInfo)
  const isPendingVerification =
    userInfo?.account.statusKYC === PersonaInquiryStatusEnum.PENDING ||
    userInfo?.account.statusKYC === PersonaInquiryStatusEnum.COMPLETED ||
    userInfo?.account.statusKYC === PersonaInquiryStatusEnum.STARTED

  const localVersion = env.APP_VERSION

  // Rutas donde la sidebar debe estar oculta
  const hiddenSidebar =
    locationPathname === AppRoutes.setupDigitalFootprint ||
    locationPathname === AppRoutes.defaultBehaviorAndTopics ||
    locationPathname === AppRoutes.kycVerification ||
    locationPathname === AppRoutes.kycVerificationInProgress ||
    locationPathname === AppRoutes.generatingReportMock ||
    locationPathname === AppRoutes.paymentPaywall ||
    locationPathname === AppRoutes.paymentSuccess ||
    locationPathname === AppRoutes.confirmSearchInfo ||
    locationPathname === AppRoutes.editDigitalFootprint ||
    locationPathname === AppRoutes.digitalReport ||
    locationPathname === AppRoutes.socialMediaProfile ||
    locationPathname === AppRoutes.generatingReport ||
    locationPathname === AppRoutes.settingsVerifyPhone ||
    locationPathname === AppRoutes.settingsVerifyPhoneOTP ||
    locationPathname === AppRoutes.impersonationConfirmInfo ||
    locationPathname === AppRoutes.impersonationScreening ||
    locationPathname === AppRoutes.impersonationPaywall ||
    locationPathname === AppRoutes.impersonationPaymentSuccess ||
    locationPathname === AppRoutes.impersonationGeneratingReport ||
    locationPathname === AppRoutes.impersonationReport

  useCheckSession()

  const [
    triggerGetUserInfo,
    { isLoading: isLoadingUserData, data: userData, isSuccess },
  ] = useLazyUserInfoQuery({
    pollingInterval: isPendingVerification ? 5000 : 0,
  })

  const isPending = selectIsPending(userData)
  const userFullname = selectUserFullname(userData)
  const userFirstLetter = selectUserFirstLetter(userData)
  const credits = userData?.account?.credits ?? 0

  const {
    i18n: { changeLanguage },
  } = useTranslation()

  useEffect(() => {
    triggerGetUserInfo()
  }, [locationPathname, triggerGetUserInfo])

  useStatusGuard()

  useEffect(() => {
    if (userData) {
      dispatch(setUserInfo(userData))
      changeLanguage(userData.language)
    }
  }, [userData, isSuccess])

  if (isLoadingUserData || !userData) {
    return (
      <div className="flex h-screen items-center">
        <div className="flex justify-center w-full">
          <Loader />
        </div>
      </div>
    )
  }

  return (
    <SidebarProvider defaultOpen={!isSmallDevice}>
      {!hiddenSidebar && (
        <AppSidebar
          userData={userData}
          isLoadingUserData={isLoadingUserData}
          isPending={isPending}
          userFullname={userFullname}
          userFirstLetter={userFirstLetter}
          credits={credits}
        />
      )}

      <main className="flex-1 w-full overflow-hidden relative">
        {!hiddenSidebar && isMobileQuery && (
          <div className="fixed top-0 left-0 right-0 z-40 p-5 bg-baseColorHighlight/80 backdrop-blur-sm">
            <SidebarTrigger />
          </div>
        )}

        {!isSmallDevice &&
          compareVersions(
            remoteConfigForceReloadVersion.version,
            localVersion,
          ) > 0 && (
            <div className="fixed top-0 left-[var(--sidebar-width)] right-0 z-20">
              <ForceReloadCard />
            </div>
          )}
        {isSmallDevice &&
          compareVersions(
            remoteConfigForceReloadVersion.version,
            localVersion,
          ) > 0 && (
            <div className="fixed top-0 left-0 right-0 z-50">
              <ForceReloadCard />
            </div>
          )}

        <div
          className={`relative w-full h-screen overflow-auto custom-scrollbar ${
            !hiddenSidebar && isMobileQuery ? 'pt-20' : ''
          }`}
        >
          <div
            className="fixed inset-0 bg-cover bg-no-repeat bg-center pointer-events-none"
            style={{
              backgroundImage: `url(${backgroundImage1})`,
              zIndex: -2,
            }}
          />

          <div
            className="fixed inset-0 bg-cover bg-no-repeat bg-center animate-bg-scroll pointer-events-none"
            style={{
              backgroundImage: `url(${backgroundImage2})`,
              zIndex: -1,
            }}
          />

          <div className="relative h-full">
            <Outlet />
          </div>
        </div>
      </main>
    </SidebarProvider>
  )
}
